import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { arrayMoveImmutable } from "array-move";

import SortableList from "./components/SortableList";

import { Drawer } from "@components/common";
import { Box, Button, Card, Stack } from "@components/base";
import { useSortSectionQuestions } from "@api";
import { useMultistepFormStore } from "@components/common/MultistepForm";
import { useUserStore } from "@store/user";

const SortQuestionsDrawer: FC = () => {
  const { t } = useTranslation();
  const {
    sortQuestionsOpen,
    setSortQuestionsOpen,
    activeSection,
    formSettings: {
      formBasics: { id },
    },
  } = useMultistepFormStore();
  const [questions, setQuestions] = useState(activeSection?.formQuestions || []);
  const { user } = useUserStore();
  const {
    sortSectionQuestionsAsync,
    sortSectionQuestionsEnabled,
    isPendingSortSectionQuestions,
  } = useSortSectionQuestions(user, id);

  const handleSaveSortQuestions = async (): Promise<void> => {
    if (activeSection) {
      await sortSectionQuestionsAsync({
        sectionId: activeSection.id,
        dto: {
          questionIds: questions.map((q) => q.formQuestionId),
        },
      });
    }
    setSortQuestionsOpen(false);
  };

  const onClose = (): void => {
    setSortQuestionsOpen(false);
  };

  useEffect(() => {
    setQuestions(activeSection?.formQuestions || []);
  }, [activeSection, open]);

  return (
    sortSectionQuestionsEnabled && (
      <Drawer
        open={sortQuestionsOpen}
        onClose={onClose}
        title={t("multistep.sortQuestions")}
        content={
          <Stack spacing={2}>
            <Card color="grey">
              <Box p={2} sx={{ cursor: "move" }}>
                <SortableList
                  items={questions}
                  onSortEnd={({ oldIndex, newIndex }) =>
                    setQuestions(arrayMoveImmutable(questions, oldIndex, newIndex))
                  }
                />
              </Box>
            </Card>
            <Stack direction="row" justifyContent="space-between">
              <Button variant="outlined" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleSaveSortQuestions}
                isLoading={isPendingSortSectionQuestions}
              >
                {t("multistep.saveSortQuestions")}
              </Button>
            </Stack>
          </Stack>
        }
      />
    )
  );
};

export default SortQuestionsDrawer;
